import { ReactElement } from 'react'
import { Close } from '@mui/icons-material'
import { Box, IconButton, Tooltip } from '@mui/material'
import { useResultsSelection } from '@editorHooks'
import useDesignFormNavigationGuard from '../hooks/useDesignFormNavigationGuard'

type Props = {
  /** Reactive disable — the main detail pane exposes react-hook-form `isDirty` at the tab-bar level. */
  disabled?: boolean
}

/**
 * Trailing "X" for the detail-pane tab bar that clears the current element selection (same action as
 * ESC), giving a click target to get back to the element list. Rendered absolutely, so the caller's
 * tab-bar wrapper must be `position: relative`.
 *
 * Panes without a reactive dirty flag (assembly-surface, standard-rips, stiffening) leave the button
 * enabled; a click while the design form has unsaved changes shows the "unsaved changes" snackbar
 * (same as ESC) instead of deselecting.
 */
const Results2DetailCloseButton = ({ disabled = false }: Props): ReactElement => {
  const { resetResultsSelection } = useResultsSelection()
  const guardDesignFormNav = useDesignFormNavigationGuard()

  const tooltip = disabled
    ? 'Nicht gespeicherte Änderungen – bitte zuerst speichern'
    : 'Auswahl aufheben'

  const handleClick = (): void => {
    if (guardDesignFormNav()) return
    resetResultsSelection()
  }

  return (
    <Tooltip title={tooltip} arrow disableInteractive placement="top">
      {/* Box wrapper keeps the tooltip working while the button is disabled. */}
      <Box
        sx={{
          position: 'absolute',
          right: 6,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'inline-flex',
          zIndex: 1,
        }}
      >
        <IconButton
          size="small"
          disabled={disabled}
          onClick={handleClick}
          aria-label="Auswahl aufheben"
          data-cy="results2-detail-close"
          sx={{ color: 'text.secondary' }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Tooltip>
  )
}

export default Results2DetailCloseButton
